import { Injectable, inject } from "@angular/core";
import { AuthState } from "../../Auth/State/AuthState";
import { UserApi } from "../Api/UserApi";
import { ProfileState } from "./ProfileState";
import { UserListItem } from "../Dto/UserModel";
import { createBackendError, getBackendErrorMessage } from "../../../Common/Utility/UtilityError";

@Injectable({ providedIn: "root" })
export class ProfileEffects {
  private readonly userApi = inject(UserApi);
  private readonly profileState = inject(ProfileState);
  private readonly authState = inject(AuthState);

  async fetchProfile(userId: string): Promise<boolean> {
    this.profileState.setLoading(true);
    this.profileState.setError(null);

    try {
      const profile = await this.userApi.getProfile(userId);
      this.profileState.setProfile(profile);
      this.syncAuthUser(profile);
      return true;
    } catch (error: unknown) {
      this.profileState.setError(getBackendErrorMessage(error, "An unexpected error occurred"));
      return false;
    } finally {
      this.profileState.setLoading(false);
    }
  }

  async updateProfile(userId: string, profileData: Partial<UserListItem>): Promise<boolean> {
    this.profileState.setLoading(true);
    this.profileState.setError(null);

    try {
      await this.userApi.updateProfile(userId, profileData);
      const current = this.profileState.profile();
      if (current) {
        const updated = { ...current, ...profileData };
        this.profileState.setProfile(updated);
        this.syncAuthUser(updated);
      }
      return true;
    } catch (error: unknown) {
      this.profileState.setError(getBackendErrorMessage(error, "An unexpected error occurred"));
      throw createBackendError(error, "An unexpected error occurred");
    } finally {
      this.profileState.setLoading(false);
    }
  }

  async deleteImage(): Promise<boolean> {
    this.profileState.setLoading(true);
    this.profileState.setError(null);

    try {
      await this.userApi.deleteImage();
      const current = this.profileState.profile();
      if (current) {
        const updated = { ...current, imageUrl: null };
        this.profileState.setProfile(updated);
        this.syncAuthUser(updated);
      }
      return true;
    } catch (error: unknown) {
      this.profileState.setError(getBackendErrorMessage(error, "An unexpected error occurred"));
      throw createBackendError(error, "An unexpected error occurred");
    } finally {
      this.profileState.setLoading(false);
    }
  }

  private syncAuthUser(profile: UserListItem): void {
    this.authState.updateUser({
      name: profile.name,
      email: profile.email,
      phone: profile.phone ?? null,
      address: profile.address ?? null,
      imageUrl: profile.imageUrl ?? null,
    });
  }
}
